import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingIndicator,
} from '@commenda/kakashi';
import { OnboardingStepNotificationEntity } from '@commenda/types/src/company/types';
import { AddEntityFormFields } from './AddEntityFormFields';
import { AddEntityFormData, useAddEntityForm } from './useAddEntityForm';

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId?: number;
  companyId: number;
  shouldNotResetForm?: boolean;
  onSuccess?: (corporation: OnboardingStepNotificationEntity) => Promise<void>;
  defaultValues?: Partial<AddEntityFormData>;
  disabledFields?: Partial<Record<keyof AddEntityFormData, boolean>>;
  allowEmptyEntityName?: boolean;
  mode?: 'admin' | 'user';
}

const AddEntityModal = ({
  isOpen,
  onClose,
  businessEntityId,
  companyId,
  shouldNotResetForm,
  onSuccess,
  defaultValues,
  disabledFields,
  allowEmptyEntityName,
  mode,
}: AddEntityModalProps) => {
  const form = useAddEntityForm({
    onClose,
    companyId,
    businessEntityId,
    shouldNotResetForm,
    onSuccess,
    defaultValues,
    allowEmptyEntityName,
    mode,
  });
  const title = businessEntityId ? 'Edit entity' : 'Add entity';

  if (form.isLoading) {
    if (form.loadError) console.error(form.loadError);
    return (
      <Dialog open={isOpen} onOpenChange={form.requestClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            <LoadingIndicator size={64} />
          </div>
          <DialogFooter>
            <Button variant="secondary" size="md" onClick={form.requestClose}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={form.requestClose}>
      <DialogContent
        preventCloseOnOutsideClick
        className="w-[600px] flex flex-col overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <AddEntityFormFields
            form={form}
            disabledFields={disabledFields}
            className="flex-1 min-h-0 overflow-y-auto px-6 py-5 max-h-[440px]"
          />
          <DialogFooter className="mt-auto">
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={form.requestClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={form.isSubmitting || !form.isDirty}
            >
              {form.isSubmitting ? <LoadingIndicator size={24} /> : null}
              <span>{businessEntityId ? 'Confirm changes' : 'Confirm'}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { AddEntityModal };
export type { AddEntityFormData };
